import { useContext, useEffect, useMemo, useRef, useState } from "react"
import { AuthContext } from "../../utils/providers/auth"
import { auth } from "../../utils/firebase"
import "./case-lookup.scss"

// UI imports
import Tile from "../../components/structure/tile/tile"
import Title from "../../components/structure/title/title"
import Table from "../../components/structure/table/table"
import Button from "../../components/ui/button/button"
import Input from "../../components/ui/inputs/input"
import Badge from "../../components/ui/badge/badge"
import Checkbox from "../../components/ui/checkbox/checkbox"

// Lookup pipeline
import { fetchAudiences, fetchArchivedIndex, makeCampaignResolver, verifyKey } from "./_utils/mailchimp"
import { buildClientIndex, fetchStaff } from "./_utils/firestore-lookup"
import { readEmailsFromWorkbook } from "./_utils/workbook-in"
import { buildResultsWorkbook } from "./_utils/workbook-out"
import { buildReportPDF, formatMoney } from "./_utils/pdf-report"
import { buildRunInfo, processAddress, runPool, STAGES } from "./_utils/run"
import { ensureWritePermission, isDirectoryPickerSupported, pickOutputDirectory, writeOutputFile } from "./_utils/output-directory"

/**
 * Session storage, not local storage: the Mailchimp key survives a page refresh
 * while the tab is open and is gone the moment it is closed.
 */
const KEY_STORAGE = "case-lookup-mailchimp-key"

/**
 * Case lookup: a single-page tool for answering a request for everything held
 * against a list of email addresses.
 *
 * Drop in a spreadsheet of addresses, connect a Mailchimp key, and it walks each
 * address through Mailchimp and the booking system, writing a PDF report, a
 * results workbook and the raw findings as JSON into a folder you choose.
 *
 * Two deliberate constraints:
 *   - Mailchimp is reached through the dev-server proxy in src/setupProxy.js,
 *     because the Mailchimp API sends no CORS headers. The page therefore only
 *     works under `npm start`, and nothing had to be deployed for it to exist.
 *   - Every lookup is a read. Nothing on this page writes to Firestore or to
 *     Mailchimp.
 */
export default function CaseLookup() {
	const { user } = useContext(AuthContext)

	// Mailchimp connection
	const [apiKey, setApiKey] = useState(() => sessionStorage.getItem(KEY_STORAGE) || "")
	const [connecting, setConnecting] = useState(false)
	const [account, setAccount] = useState(null)
	const [keyError, setKeyError] = useState("")

	// Output folder
	const [directoryHandle, setDirectoryHandle] = useState(null)
	const [directoryName, setDirectoryName] = useState("")

	// Run inputs
	const [caseReference, setCaseReference] = useState("")
	const [parsed, setParsed] = useState(null)
	const [parseError, setParseError] = useState("")
	const [scanArchived, setScanArchived] = useState(true)
	const [fullClientScan, setFullClientScan] = useState(true)

	// Run state
	const [running, setRunning] = useState(false)
	const [preflight, setPreflight] = useState([])
	const [rows, setRows] = useState([])
	const [results, setResults] = useState(null)
	const [runInfo, setRunInfo] = useState(null)
	const [outputs, setOutputs] = useState([])
	const [runError, setRunError] = useState("")
	const [writing, setWriting] = useState(false)

	const fileInput = useRef(null)

	const operator = useMemo(() => {
		const name = [user?.first_name, user?.last_name].filter(Boolean).join(" ")

		return [name, auth.currentUser?.email].filter(Boolean).join(" - ") || "Unknown operator"
	}, [user])

	useEffect(() => {
		document.title = "Case lookup"
	}, [])

	/**
	 * Add a line to the preflight log. Preflight work happens once per run and is
	 * where the slow, whole-collection passes live, so it gets its own running
	 * commentary rather than being hidden behind a spinner.
	 */
	const log = (message) => setPreflight((current) => [...current, { at: new Date().toLocaleTimeString("en-GB"), message }])

	const replaceLastLog = (message) =>
		setPreflight((current) => {
			const next = [...current]
			next[next.length - 1] = { at: next[next.length - 1]?.at || new Date().toLocaleTimeString("en-GB"), message }
			return next
		})

	/**
	 * Check the key before the run rather than discovering it is wrong on the
	 * first address.
	 */
	const connect = async () => {
		setConnecting(true)
		setKeyError("")
		setAccount(null)

		try {
			const details = await verifyKey(apiKey.trim())

			setAccount(details)
			sessionStorage.setItem(KEY_STORAGE, apiKey.trim())
		} catch (error) {
			setKeyError(error.message)
		}

		setConnecting(false)
	}

	const forgetKey = () => {
		sessionStorage.removeItem(KEY_STORAGE)
		setApiKey("")
		setAccount(null)
		setKeyError("")
	}

	const chooseFolder = async () => {
		try {
			const handle = await pickOutputDirectory()

			if (!handle) return

			setDirectoryHandle(handle)
			setDirectoryName(handle.name)
		} catch (error) {
			setRunError(`Could not use that folder: ${error.message}`)
		}
	}

	const onFileChosen = async (file) => {
		if (!file) return

		setParseError("")
		setParsed(null)
		setResults(null)
		setOutputs([])
		setRows([])

		try {
			const contents = await readEmailsFromWorkbook(file)

			if (contents.emails.length === 0) {
				setParseError("No email addresses were found in that file. Check the addresses are in a column of their own.")
				return
			}

			setParsed(contents)
			setRows(
				contents.emails.map((entry) => ({
					email: entry.email,
					source: entry.source,
					status: "QUEUED",
					stages: {},
					summary: "",
				}))
			)

			if (!caseReference) setCaseReference(file.name.replace(/\.[^.]+$/, ""))
		} catch (error) {
			setParseError(`Could not read that file: ${error.message}`)
		}
	}

	const updateRow = (index, stageKey, state, detail) =>
		setRows((current) => {
			const next = [...current]
			const row = next[index]

			if (!row) return current

			next[index] = {
				...row,
				status: "RUNNING",
				stages: { ...row.stages, [stageKey]: { state, detail } },
			}

			return next
		})

	const completeRow = (index, result) =>
		setRows((current) => {
			const next = [...current]

			if (!next[index]) return current

			next[index] = {
				...next[index],
				status: result.status,
				summary:
					result.status === "ERROR"
						? result.error
						: [
								`${result.clients.length} account(s)`,
								`${result.enquiries.length} enquiry(ies)`,
								`${result.bookings.length} booking(s)`,
								formatMoney(result.totals.lifetimeValue) || "£0.00",
						  ].join(", "),
			}

			return next
		})

	/**
	 * Write the three output files. Kept separate from the run so the operator can
	 * write again without repeating every lookup, which matters if the folder
	 * permission lapsed or the wrong folder was picked.
	 */
	const writeOutputs = async (finalResults, info) => {
		setWriting(true)
		setRunError("")

		try {
			let handle = directoryHandle

			if (handle && !(await ensureWritePermission(handle))) {
				handle = null
				log("Write permission on the chosen folder was refused - falling back to downloads.")
			}

			const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")
			const slug = `${(info.caseReference || "case-lookup").replace(/[^a-zA-Z0-9-_]+/g, "-").slice(0, 60)}_${stamp}`

			const pdf = await buildReportPDF(finalResults, info)
			const workbook = buildResultsWorkbook(finalResults, info)
			const json = new Blob([JSON.stringify({ runInfo: info, results: finalResults }, null, 2)], { type: "application/json" })

			const written = []

			written.push(await writeOutputFile(handle, `${slug}-report.pdf`, pdf))
			written.push(await writeOutputFile(handle, `${slug}-results.xlsx`, workbook))
			written.push(await writeOutputFile(handle, `${slug}-raw-findings.json`, json))

			setOutputs(written)
		} catch (error) {
			setRunError(`Could not write the output files: ${error.message}`)
		}

		setWriting(false)
	}

	const startRun = async () => {
		if (!parsed) return

		setRunning(true)
		setRunError("")
		setPreflight([])
		setOutputs([])
		setResults(null)

		const generatedAt = new Date().toISOString()
		const key = account ? apiKey.trim() : ""

		try {
			log("Loading staff records for agent names")
			const staff = await fetchStaff()

			let audiences = []
			let archivedIndex = null

			if (key) {
				log("Listing Mailchimp audiences")
				audiences = await fetchAudiences(key)
				replaceLastLog(`Found ${audiences.length} Mailchimp audience(s): ${audiences.map((audience) => audience.name).join(", ")}`)

				if (scanArchived) {
					log("Indexing archived Mailchimp contacts")
					archivedIndex = await fetchArchivedIndex(key, audiences, ({ audience, scanned, total }) => replaceLastLog(`Indexing archived contacts in ${audience}: ${scanned} of ${total}`))
					replaceLastLog(`Indexed archived contacts across ${audiences.length} audience(s) - ${archivedIndex.size} address(es) held only in the archive`)
				}
			} else {
				log("No Mailchimp key connected - marketing records will not be checked")
			}

			let clientIndex = null
			let clientIndexScanned = 0

			if (fullClientScan) {
				log("Reading the clients collection so email matching ignores capitalisation")
				const scan = await buildClientIndex(({ scanned }) => replaceLastLog(`Reading client accounts: ${scanned.toLocaleString("en-GB")} read`))
				clientIndex = scan.index
				clientIndexScanned = scan.scanned
				replaceLastLog(`Read ${scan.scanned.toLocaleString("en-GB")} client accounts (${scan.index.size.toLocaleString("en-GB")} distinct addresses)`)
			}

			const context = {
				apiKey: key,
				audiences,
				archivedIndex,
				resolveCampaign: makeCampaignResolver(key),
				staff,
				clientIndex,
			}

			log(`Checking ${parsed.emails.length} address(es)`)

			const finalResults = await runPool(parsed.emails, async (entry, index) => {
				const result = await processAddress(entry, context, (stageKey, state, detail) => updateRow(index, stageKey, state, detail))

				completeRow(index, result)

				return result
			})

			const info = buildRunInfo({
				caseReference: caseReference || "Case lookup",
				operator,
				sourceFile: parsed.fileName,
				sourceRows: parsed.totalRows,
				generatedAt,
				audiences,
				mailchimpAccount: account?.accountName || "",
				apiKeyUsed: Boolean(key),
				archivedScanned: Boolean(archivedIndex),
				clientIndexScanned: fullClientScan ? clientIndexScanned : 0,
				correspondenceAccessible: finalResults.some((result) => result.correspondence.accessible),
				skippedRows: parsed.skippedRows,
				sheetsScanned: parsed.sheetsScanned,
				addressCount: parsed.emails.length,
			})

			setResults(finalResults)
			setRunInfo(info)

			log("Building the report, workbook and raw findings")
			await writeOutputs(finalResults, info)
			log("Done")
		} catch (error) {
			setRunError(error.message || String(error))
		}

		setRunning(false)
	}

	const completed = rows.filter((row) => row.status === "COMPLETE" || row.status === "ERROR").length
	const failed = rows.filter((row) => row.status === "ERROR").length
	const percent = rows.length > 0 ? Math.round((completed / rows.length) * 100) : 0

	const totals = useMemo(() => {
		if (!results) return null

		return {
			onLists: results.filter((result) => (result.mailchimp?.memberships || []).length > 0).length,
			subscribed: results.filter((result) => (result.mailchimp?.memberships || []).some((membership) => membership.status === "subscribed")).length,
			accounts: results.filter((result) => result.clients.length > 0).length,
			bookings: results.filter((result) => result.bookings.length > 0).length,
			value: results.reduce((total, result) => total + result.totals.lifetimeValue, 0),
		}
	}, [results])

	return (
		<>
			<Tile fullPage={true}>
				<Title hasBorder={true}>
					<h1>Case lookup</h1>
					<p>
						Everything held against a list of email addresses: marketing subscriptions and campaign history from Mailchimp, and accounts, enquiries, bookings, quotes and lifetime value from the
						booking system. Produces a PDF report, a results workbook and the raw findings.
					</p>
				</Title>

				<div className="case-lookup-notice">
					<strong>Runs locally only.</strong> Mailchimp refuses browser requests, so calls are relayed through the development server. This page works under <code>npm start</code> and does nothing
					on a deployed build. Every lookup is read-only.
				</div>

				{/* Step 1 - Mailchimp */}
				<div className="case-lookup-step">
					<div className="case-lookup-step-number">1</div>
					<div className="case-lookup-step-body">
						<h2>Connect Mailchimp</h2>
						<p>Paste an API key. It is held for this browser tab only, never written to disk and never included in a build. Leave it empty to check the booking system alone.</p>

						<div className="case-lookup-inline">
							<Input
								type="password"
								placeholder="Mailchimp API key:"
								value={apiKey}
								onChange={setApiKey}
								error={keyError}
							/>
							<Button
								label={account ? "Reconnect" : "Connect"}
								loading={connecting}
								loadingText="Checking"
								disabled={!apiKey.trim() || connecting}
								onClick={() => connect()}
							/>
							{(account || apiKey) && (
								<Button
									label="Forget key"
									type="NEGATIVE"
									onClick={() => forgetKey()}
								/>
							)}
						</div>

						{account && (
							<p className="case-lookup-success">
								Connected to <strong>{account.accountName || "Mailchimp"}</strong>
								{account.totalSubscribers !== null && ` - ${account.totalSubscribers.toLocaleString("en-GB")} subscribers on the account`}
								{account.health && ` (${account.health})`}
							</p>
						)}
					</div>
				</div>

				{/* Step 2 - output folder */}
				<div className="case-lookup-step">
					<div className="case-lookup-step-number">2</div>
					<div className="case-lookup-step-body">
						<h2>Choose where the results go</h2>
						<p>Pick the folder holding the source spreadsheet and the report, workbook and raw findings are written straight into it. Without a folder they download instead.</p>

						<div className="case-lookup-inline">
							<Button
								label={directoryName ? `Folder: ${directoryName}` : "Choose folder"}
								onClick={() => chooseFolder()}
								disabled={!isDirectoryPickerSupported()}
							/>
						</div>

						{!isDirectoryPickerSupported() && <p className="case-lookup-warning">This browser cannot write to a folder directly. The files will download instead - use Chrome or Edge to write in place.</p>}
					</div>
				</div>

				{/* Step 3 - the file */}
				<div className="case-lookup-step">
					<div className="case-lookup-step-number">3</div>
					<div className="case-lookup-step-body">
						<h2>Drop in the spreadsheet</h2>
						<p>Any .xlsx or .csv file. Every sheet is scanned and the column holding the most email addresses is used, so a header row is welcome but not needed.</p>

						<div
							className="case-lookup-drop"
							onDragOver={(event) => event.preventDefault()}
							onDrop={(event) => {
								event.preventDefault()
								onFileChosen(event.dataTransfer.files?.[0])
							}}
							onClick={() => fileInput.current?.click()}>
							<input
								ref={fileInput}
								type="file"
								accept=".xlsx,.xls,.csv"
								onChange={(event) => onFileChosen(event.target.files?.[0])}
							/>
							{parsed ? (
								<div>
									<strong>{parsed.fileName}</strong>
									<span>
										{parsed.emails.length} address(es) to check from {parsed.totalRows} row(s)
										{parsed.skippedRows > 0 && `, ${parsed.skippedRows} row(s) skipped as not an email address`}
									</span>
								</div>
							) : (
								<div>
									<strong>Drop the file here</strong>
									<span>or click to browse</span>
								</div>
							)}
						</div>

						{parseError && <p className="case-lookup-warning">{parseError}</p>}

						{parsed && (
							<Table
								className="case-lookup-sheets"
								headings={["Sheet", "Rows", "Email column", "Addresses seen"]}>
								{parsed.sheetsScanned.map((sheet) => (
									<tr key={sheet.sheetName}>
										<td>{sheet.sheetName}</td>
										<td>{sheet.rows}</td>
										<td>{sheet.emailColumn === -1 ? <small className="no-value">No email column</small> : `Column ${sheet.emailColumn + 1}`}</td>
										<td>{sheet.emailsFound}</td>
									</tr>
								))}
							</Table>
						)}
					</div>
				</div>

				{/* Step 4 - run */}
				<div className="case-lookup-step">
					<div className="case-lookup-step-number">4</div>
					<div className="case-lookup-step-body">
						<h2>Run the checks</h2>

						<div className="case-lookup-inline">
							<Input
								type="text"
								placeholder="Case reference:"
								value={caseReference}
								onChange={setCaseReference}
								note="Printed on every page of the report and used in the file names"
							/>
						</div>

						<div className="case-lookup-options">
							<Checkbox
								label="Index archived Mailchimp contacts (slower, but an archived contact is invisible to Mailchimp's search without it)"
								checked={scanArchived}
								onClick={() => setScanArchived(!scanArchived)}
							/>
							<Checkbox
								label="Read the whole clients collection (slower, but the only way to match addresses regardless of how they were capitalised)"
								checked={fullClientScan}
								onClick={() => setFullClientScan(!fullClientScan)}
							/>
						</div>

						<div className="case-lookup-inline">
							<Button
								label="Start lookup"
								loading={running}
								loadingText="Working"
								disabled={!parsed || running}
								onClick={() => startRun()}
							/>
							{results && (
								<Button
									label="Write the files again"
									loading={writing}
									loadingText="Writing"
									disabled={writing || running}
									onClick={() => writeOutputs(results, runInfo)}
								/>
							)}
						</div>

						{runError && <p className="case-lookup-warning">{runError}</p>}
					</div>
				</div>
			</Tile>

			{/* Progress */}
			{(running || rows.length > 0) && (
				<Tile fullPage={true}>
					<Title hasBorder={true}>
						<h2>Progress</h2>
						<p>
							{completed} of {rows.length} address(es) checked{failed > 0 && `, ${failed} could not be completed`}
						</p>
					</Title>

					<div className="case-lookup-progress">
						<div
							className="case-lookup-progress-bar"
							style={{ width: `${percent}%` }}
						/>
						<span>{percent}%</span>
					</div>

					{preflight.length > 0 && (
						<div className="case-lookup-log">
							{preflight.map((entry, index) => (
								<div key={index}>
									<span>{entry.at}</span>
									{entry.message}
								</div>
							))}
						</div>
					)}

					<Table
						className="case-lookup-rows"
						headings={["Email address", "Source", ...STAGES.map((stage) => stage.label), "Result"]}>
						{rows.slice(0, 300).map((row, index) => (
							<tr key={`${row.email}-${index}`}>
								<td>{row.email}</td>
								<td>
									<small>{row.source}</small>
								</td>
								{STAGES.map((stage) => {
									const state = row.stages[stage.key]

									return (
										<td key={stage.key}>
											<span
												className={["case-lookup-stage", state?.state && `is-${state.state}`].join(" ")}
												title={state?.detail || ""}>
												{state?.state === "done" ? state.detail || "ok" : state?.state === "running" ? "..." : state?.state === "failed" ? "failed" : state?.state === "skipped" ? "-" : ""}
											</span>
										</td>
									)
								})}
								<td>
									{row.status === "QUEUED" && <Badge label="Queued" />}
									{row.status === "RUNNING" && (
										<Badge
											label="Checking"
											type="INFO"
										/>
									)}
									{row.status === "COMPLETE" && <small>{row.summary}</small>}
									{row.status === "ERROR" && (
										<Badge
											label={row.summary || "Error"}
											type="NEGATIVE"
										/>
									)}
								</td>
							</tr>
						))}
					</Table>

					{rows.length > 300 && <p className="case-lookup-warning">Showing the first 300 addresses. All {rows.length} are processed and appear in the outputs.</p>}
				</Tile>
			)}

			{/* Outcome */}
			{results && (
				<Tile fullPage={true}>
					<Title hasBorder={true}>
						<h2>Findings</h2>
					</Title>

					{totals && (
						<Table headings={["Measure", "Result"]}>
							<tr>
								<td>Addresses checked</td>
								<td>{results.length}</td>
							</tr>
							<tr>
								<td>Found on a mailing list</td>
								<td>{totals.onLists}</td>
							</tr>
							<tr>
								<td>Currently subscribed</td>
								<td>{totals.subscribed}</td>
							</tr>
							<tr>
								<td>With a booking system account</td>
								<td>{totals.accounts}</td>
							</tr>
							<tr>
								<td>With at least one booking</td>
								<td>{totals.bookings}</td>
							</tr>
							<tr>
								<td>Combined lifetime value</td>
								<td>{formatMoney(totals.value)}</td>
							</tr>
						</Table>
					)}

					{outputs.length > 0 && (
						<div className="case-lookup-outputs">
							<h3>Files written</h3>
							<ul>
								{outputs.map((output) => (
									<li key={output.fileName}>
										{output.fileName}
										<Badge
											label={output.destination === "folder" ? directoryName : "Downloads"}
											type={output.destination === "folder" ? "POSITIVE" : "AMBER"}
										/>
									</li>
								))}
							</ul>
						</div>
					)}
				</Tile>
			)}
		</>
	)
}
